import { useCallback, useEffect, useState } from 'react';
import { AlertTriangle, ChevronDown, Loader2, ShoppingCart } from 'lucide-react';
import { cn } from '../../lib/cn';
import { eventiService } from '../../lib/eventi/eventiService';
import { messaggioErrore } from '../../lib/eventi/errori';
import { statoFattura, type StatoFattura } from '../../lib/eventi/fatturaOrdine';
import type { OrdineDelSito } from '../../lib/eventi/tipi';
import { CartaOrdine } from './CartaOrdine';

/** Gli stati che chiedono qualcosa a chi fattura. */
const DA_GUARDARE: StatoFattura['stato'][] = ['da_fare', 'errore', 'incerta', 'in_creazione', 'nota_di_credito'];

type Filtro = 'da_guardare' | 'tutti';

/**
 * Fatture → Ordini dal sito: gli ordini pagati su slabsrl.com, con la stessa
 * carta e lo stesso pulsante della scheda Biglietti di un evento. Serve a chi
 * fattura (anche l'amministrazione, che non apre gli eventi). Parte chiuso:
 * il bollino "da fatturare" dice se c'e' qualcosa da fare. Sfondo lilla
 * chiaro e bordo tratteggiato, perche' non si confonda con le righe bianche
 * a bordo pieno dei documenti nella lista sotto.
 */
export function OrdiniDalSito() {
  const [ordini, setOrdini] = useState<OrdineDelSito[] | null>(null);
  const [errore, setErrore] = useState<string | null>(null);
  const [filtro, setFiltro] = useState<Filtro>('da_guardare');
  const [aperto, setAperto] = useState(false);

  const carica = useCallback(async () => {
    try {
      setOrdini(await eventiService.ordiniDelSito());
      setErrore(null);
    } catch (e) {
      setErrore(messaggioErrore(e));
    }
  }, []);

  useEffect(() => {
    void carica();
  }, [carica]);

  if (ordini !== null && ordini.length === 0 && !errore) return null;

  const adesso = new Date();
  const daGuardare = (ordini ?? []).filter((o) => DA_GUARDARE.includes(statoFattura(o.ordine, adesso).stato));
  const visibili = filtro === 'da_guardare' ? daGuardare : ordini ?? [];
  const espanso = aperto;

  return (
    <section className="overflow-hidden rounded-2xl border border-dashed border-violet-300 bg-violet-50/60">
      <button
        type="button"
        onClick={() => setAperto(!espanso)}
        aria-expanded={espanso}
        className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-violet-100/50 md:px-5"
      >
        <span className="flex h-9 w-9 flex-shrink-0 items-center justify-center rounded-full bg-white text-violet-600 ring-1 ring-violet-200">
          <ShoppingCart className="h-4 w-4" />
        </span>
        <span className="min-w-0 flex-1">
          <span className="block text-sm font-semibold text-violet-900">Ordini dal sito slabsrl.com</span>
          <span className="block text-xs text-violet-700/70">Biglietti pagati online: la fattura nasce su Fatture in Cloud già pagata.</span>
        </span>
        {ordini === null && !errore ? (
          <Loader2 className="h-4 w-4 animate-spin text-violet-400" />
        ) : (
          <span
            className={cn(
              'rounded-full border px-2.5 py-0.5 text-xs font-semibold whitespace-nowrap',
              daGuardare.length > 0 ? 'border-violet-300 bg-white text-violet-700' : 'border-green-200 bg-green-50 text-green-700',
            )}
          >
            {daGuardare.length > 0 ? `${daGuardare.length} da fatturare` : 'Tutto fatturato'}
          </span>
        )}
        <ChevronDown className={cn('h-4 w-4 flex-shrink-0 text-violet-400 transition-transform', espanso && 'rotate-180')} />
      </button>

      {espanso && (
        <div className="border-t border-dashed border-violet-200">
          {errore ? (
            <p className="flex items-center gap-2 px-5 py-4 text-sm text-red-700">
              <AlertTriangle className="h-4 w-4" /> Non riesco a leggere gli ordini del sito: {errore}
            </p>
          ) : (
            <>
              <div role="tablist" aria-label="Quali ordini" className="flex gap-1 px-4 pt-3 md:px-5">
                {([
                  { valore: 'da_guardare', testo: `Da fatturare (${daGuardare.length})` },
                  { valore: 'tutti', testo: `Tutti (${ordini?.length ?? 0})` },
                ] as const).map((f) => (
                  <button
                    key={f.valore}
                    type="button"
                    role="tab"
                    aria-selected={filtro === f.valore}
                    onClick={() => setFiltro(f.valore)}
                    className={cn(
                      'rounded-md px-3 py-1.5 text-xs font-medium transition-colors',
                      filtro === f.valore ? 'bg-white text-gray-900 shadow-sm ring-1 ring-gray-200' : 'text-neutral-500 hover:text-gray-800',
                    )}
                  >
                    {f.testo}
                  </button>
                ))}
              </div>
              {visibili.length === 0 ? (
                <p className="px-5 py-6 text-center text-sm text-neutral-500">Niente da fatturare: tutti gli ordini pagati hanno la loro fattura.</p>
              ) : (
                <ul className="space-y-3 p-3 md:p-4">
                  {visibili.map(({ ordine, righe }) => (
                    <CartaOrdine key={ordine.id} ordine={ordine} onCambiata={carica}>
                      <ul className="space-y-0.5 text-sm text-gray-700">
                        {righe.map((r, i) => (
                          <li key={`${i}-${r.nome}`}>
                            <span className="font-medium">{r.quantita} ×</span> {r.nome}
                          </li>
                        ))}
                      </ul>
                    </CartaOrdine>
                  ))}
                </ul>
              )}
            </>
          )}
        </div>
      )}
    </section>
  );
}
